// FILE: components/exams/ExamLeaderboard.tsx
"use client";

import { useState, useEffect } from "react";
import {
  Trophy, Clock, Target, Users, Eye, Loader2,
  ChevronLeft, ChevronRight, Medal,
} from "lucide-react";

interface Submission {
  id: string;
  user_id: string;
  total_questions: number;
  correct_count: number;
  wrong_count: number;
  skipped_count: number;
  score: number;
  time_spent: number;
  answers: any[];
  submitted_at: string;
  user: { full_name: string | null; username: string | null; avatar_url: string | null } | null;
}

const PAGE_SIZE = 10;

function formatDuration(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return m > 0 ? `${m}p ${s}s` : `${s}s`;
}

function relativeTime(dateStr: string): string {
  const diff = Math.floor((Date.now() - new Date(dateStr).getTime()) / 1000);
  if (diff < 60) return "Vừa xong";
  if (diff < 3600) return `${Math.floor(diff / 60)} phút trước`;
  if (diff < 86400) return `${Math.floor(diff / 3600)} giờ trước`;
  const days = Math.floor(diff / 86400);
  if (days < 7) return `${days} ngày trước`;
  return new Date(dateStr).toLocaleDateString("vi-VN");
}

export default function ExamLeaderboard({ examId, isCreator = false, onViewSubmission }: {
  examId: string;
  isCreator?: boolean;
  onViewSubmission?: (submission: Submission) => void;
}) {
  const [submissions, setSubmissions] = useState<Submission[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const res = await fetch(`/api/exam-submissions?exam_id=${examId}`);
        const data = await res.json();
        setSubmissions(data.submissions ?? []);
      } catch (err) {
        console.error("[ExamLeaderboard] fetch error:", err);
      } finally {
        setLoading(false);
      }
    })();
  }, [examId]);

  const totalPages = Math.max(1, Math.ceil(submissions.length / PAGE_SIZE));
  const paginated = submissions.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  // Stats
  const avgScore = submissions.length > 0
    ? Math.round(submissions.reduce((s, r) => s + r.score, 0) / submissions.length)
    : 0;
  const bestScore = submissions.length > 0
    ? Math.max(...submissions.map(r => r.score))
    : 0;
  const totalAttempts = submissions.length;

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="w-6 h-6 text-teal-600 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-5">
      {/* Summary cards */}
      <div className="grid grid-cols-3 gap-3">
        <div className="bg-white rounded-xl border border-[#e2e8f0] p-4 text-center">
          <Users className="w-5 h-5 text-blue-500 mx-auto mb-1.5" />
          <div className="text-[20px] font-extrabold text-[#0f172a]">{totalAttempts}</div>
          <div className="text-[11px] text-[#94a3b8]">Lượt thi</div>
        </div>
        <div className="bg-white rounded-xl border border-[#e2e8f0] p-4 text-center">
          <Target className="w-5 h-5 text-teal-500 mx-auto mb-1.5" />
          <div className="text-[20px] font-extrabold text-[#0f172a]">{avgScore}%</div>
          <div className="text-[11px] text-[#94a3b8]">Điểm TB</div>
        </div>
        <div className="bg-white rounded-xl border border-[#e2e8f0] p-4 text-center">
          <Trophy className="w-5 h-5 text-amber-500 mx-auto mb-1.5" />
          <div className="text-[20px] font-extrabold text-[#0f172a]">{bestScore}%</div>
          <div className="text-[11px] text-[#94a3b8]">Cao nhất</div>
        </div>
      </div>

      {/* Table */}
      {submissions.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-xl border border-[#e2e8f0]">
          <Users className="w-8 h-8 text-[#e2e8f0] mx-auto mb-2" />
          <p className="text-[13px] text-[#94a3b8]">Chưa có ai thi đề này</p>
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-[#e2e8f0] overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-[#f8fafc] border-b border-[#e2e8f0]">
                <tr>
                  <th className="px-4 py-3 text-left text-[11px] font-bold text-[#64748b] uppercase w-10">#</th>
                  <th className="px-4 py-3 text-left text-[11px] font-bold text-[#64748b] uppercase">Người thi</th>
                  <th className="px-4 py-3 text-center text-[11px] font-bold text-[#64748b] uppercase">Điểm</th>
                  <th className="px-4 py-3 text-center text-[11px] font-bold text-[#64748b] uppercase">Đúng/Tổng</th>
                  <th className="px-4 py-3 text-center text-[11px] font-bold text-[#64748b] uppercase">Thời gian</th>
                  <th className="px-4 py-3 text-center text-[11px] font-bold text-[#64748b] uppercase">Nộp lúc</th>
                  <th className="px-4 py-3 w-12" />
                </tr>
              </thead>
              <tbody>
                {paginated.map((sub, i) => {
                  const rank = (page - 1) * PAGE_SIZE + i + 1;
                  const name = sub.user?.full_name || sub.user?.username || "Ẩn danh";
                  const initials = name.split(" ").map(w => w[0]).join("").slice(0, 2).toUpperCase();
                  const scoreColor = sub.score >= 80 ? "text-emerald-600 bg-emerald-50" : sub.score >= 50 ? "text-amber-600 bg-amber-50" : "text-red-600 bg-red-50";

                  return (
                    <tr key={sub.id} className="border-b border-[#f8fafc] hover:bg-[#fafbfc] transition-colors">
                      <td className="px-4 py-3">
                        {rank <= 3 ? (
                          <span className={`inline-flex items-center justify-center w-6 h-6 rounded-full text-[10px] font-extrabold ${
                            rank === 1 ? "bg-amber-100 text-amber-700" : rank === 2 ? "bg-gray-100 text-gray-600" : "bg-orange-100 text-orange-700"
                          }`}>
                            {rank === 1 ? "🥇" : rank === 2 ? "🥈" : "🥉"}
                          </span>
                        ) : (
                          <span className="text-[12px] text-[#94a3b8] font-bold">{rank}</span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2.5">
                          {sub.user?.avatar_url ? (
                            <img src={sub.user.avatar_url} alt="" className="w-7 h-7 rounded-full object-cover" />
                          ) : (
                            <div className="w-7 h-7 rounded-full bg-gradient-to-br from-teal-600 to-teal-400 flex items-center justify-center text-white text-[9px] font-extrabold">
                              {initials}
                            </div>
                          )}
                          <span className="text-[13px] font-semibold text-[#0f172a] truncate max-w-[120px]">{name}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-[12px] font-extrabold ${scoreColor}`}>
                          {sub.score}%
                        </span>
                      </td>
                      <td className="px-4 py-3 text-center text-[12px] font-semibold text-[#334155]">
                        {sub.correct_count}/{sub.total_questions}
                      </td>
                      <td className="px-4 py-3 text-center">
                        <span className="inline-flex items-center gap-1 text-[12px] text-[#64748b]">
                          <Clock className="w-3 h-3" />{formatDuration(sub.time_spent)}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-center text-[11px] text-[#94a3b8]">
                        {relativeTime(sub.submitted_at)}
                      </td>
                      <td className="px-4 py-3">
                        {isCreator && (
                          <button onClick={() => onViewSubmission?.(sub)}
                            className="w-7 h-7 rounded-lg border border-[#e2e8f0] flex items-center justify-center text-[#64748b] hover:bg-teal-50 hover:text-teal-600 transition-colors"
                            title="Xem bài làm">
                            <Eye className="w-3.5 h-3.5" />
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between px-5 py-3 border-t border-[#f1f5f9]">
              <span className="text-[12px] text-[#94a3b8]">Trang {page}/{totalPages} · {submissions.length} lượt</span>
              <div className="flex items-center gap-1.5">
                <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}
                  className="w-7 h-7 rounded-lg border border-[#e2e8f0] flex items-center justify-center text-[#64748b] hover:bg-[#f1f5f9] disabled:opacity-40">
                  <ChevronLeft className="w-3.5 h-3.5" />
                </button>
                <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages}
                  className="w-7 h-7 rounded-lg border border-[#e2e8f0] flex items-center justify-center text-[#64748b] hover:bg-[#f1f5f9] disabled:opacity-40">
                  <ChevronRight className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}